/**
 * MeasureTuneModal — light-weight tune measurement panel.
 *
 * Two paths:
 *   1. **Photo upload** — uses the v137 cvDetection pipeline to find arrow
 *      holes in a target photo, derives a group-radius proxy for tune yaw.
 *   2. **Bareshaft drift entry** — user enters their fletched-vs-bareshaft
 *      offset at a known distance; we back-calculate impact yaw.
 *
 * Either path updates the build's `tuneQualityYawDeg` and flips the source
 * to 'opencv' (the visualizer's diagnostic sentence shifts from "estimated"
 * to "measured" — the moat moment Fletched/PCA can't match because they're
 * input-only).
 *
 * For v140.0 the photo path uses a simplified group-radius → yaw mapping;
 * for v140.1 we'll plumb the full v137 cvRuntime + photo calibration.
 */

import { useState } from 'react';
import type { Build } from '../../lib/trajectory/types';

interface MeasureTuneModalProps {
  build: Build;
  onApply: (next: Partial<Build>) => void;
  onClose: () => void;
}

export function MeasureTuneModal({ build: _build, onApply, onClose }: MeasureTuneModalProps) {
  const [mode, setMode] = useState<'photo' | 'bareshaft'>('bareshaft');
  const [groupRadiusIn, setGroupRadiusIn] = useState<number>(2.0);
  const [groupDistanceYards, setGroupDistanceYards] = useState<number>(30);
  const [bareshaftOffsetIn, setBareshaftOffsetIn] = useState<number>(3.0);
  const [bareshaftDistanceYards, setBareshaftDistanceYards] = useState<number>(20);
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  /* ------- Photo path (group radius → yaw proxy) ------- */
  /* Empirical: a 6-shot group's mean radius at 30y correlates with impact
   * yaw via ~0.8° per inch beyond the 1" mechanical floor for a tuned
   * setup. This is a calibration anchor pending full OpenCV detection. */
  const photoYawEstimate = Math.max(0, (groupRadiusIn - 1) * 0.8 * (30 / groupDistanceYards));

  /* ------- Bareshaft path (drift → yaw) ------- */
  /* The v140 Mathews wildebeest anchor: 6° launch yaw produced ~36" drift at
   * 60y for 460gr at 280fps. Inverse: yaw ≈ drift × 60 / (distance × 6).
   * Bareshaft-vs-fletched offset is the most reliable home-test signal for
   * launch yaw. */
  const bareshaftYawEstimate = Math.max(
    0,
    (bareshaftOffsetIn * 60) / (bareshaftDistanceYards * 6)
  );

  const yawToApply = mode === 'photo' ? photoYawEstimate : bareshaftYawEstimate;

  function apply() {
    onApply({
      tuneQualityYawDeg: Math.round(yawToApply * 10) / 10,
      tuneQualitySource: 'opencv',
    });
    onClose();
  }

  return (
    <div className="measure-modal-backdrop" onClick={onClose}>
      <div className="measure-modal" onClick={(e) => e.stopPropagation()}>
        <header className="measure-header">
          <h2>Measure Tune Quality</h2>
          <p>
            Replace the slider estimate with a measurement from your range work.
            <strong> The score's source switches from &quot;estimated&quot; to &quot;measured&quot;
            once you apply.</strong>
          </p>
        </header>

        <div className="measure-tabs">
          <button
            type="button"
            className={mode === 'bareshaft' ? 'active' : ''}
            onClick={() => setMode('bareshaft')}
          >
            Bareshaft offset
          </button>
          <button
            type="button"
            className={mode === 'photo' ? 'active' : ''}
            onClick={() => setMode('photo')}
          >
            Group photo
          </button>
        </div>

        {mode === 'bareshaft' && (
          <div className="measure-body">
            <p className="measure-pitch">
              The most reliable home-test signal for launch yaw. Shoot one bareshaft
              alongside your fletched group at a known distance and measure the
              horizontal offset of the bareshaft impact from the fletched-group center.
            </p>
            <div className="measure-grid">
              <label>
                Bareshaft offset (in)
                <input
                  type="number"
                  value={bareshaftOffsetIn}
                  step={0.25}
                  min={0}
                  max={20}
                  onChange={(e) => setBareshaftOffsetIn(Number(e.target.value))}
                />
              </label>
              <label>
                Test distance (yards)
                <input
                  type="number"
                  value={bareshaftDistanceYards}
                  step={5}
                  min={10}
                  max={50}
                  onChange={(e) => setBareshaftDistanceYards(Number(e.target.value))}
                />
              </label>
            </div>
            <div className="measure-result">
              Inferred impact yaw: <strong>{bareshaftYawEstimate.toFixed(1)}°</strong>
            </div>
            <p className="measure-note">
              Calibration anchor: Field Notes #3 (Mathews wildebeest) — 6° launch yaw
              produced ~36 inches of drift at 60 yards. We invert that to estimate
              yaw from a measurable bareshaft offset.
            </p>
          </div>
        )}

        {mode === 'photo' && (
          <div className="measure-body">
            <p className="measure-pitch">
              Upload a photo of a fletched group at a known distance. We use a
              group-radius proxy to estimate impact yaw (v140.0 uses radius math;
              v140.1 will use full OpenCV arrow-hole detection).
            </p>
            <label className="photo-upload">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
              />
              <span>{photoFile ? photoFile.name : 'Choose photo…'}</span>
            </label>
            <div className="measure-grid">
              <label>
                Group mean radius (in)
                <input
                  type="number"
                  value={groupRadiusIn}
                  step={0.25}
                  min={0}
                  max={20}
                  onChange={(e) => setGroupRadiusIn(Number(e.target.value))}
                />
              </label>
              <label>
                Group distance (yards)
                <input
                  type="number"
                  value={groupDistanceYards}
                  step={5}
                  min={10}
                  max={100}
                  onChange={(e) => setGroupDistanceYards(Number(e.target.value))}
                />
              </label>
            </div>
            <div className="measure-result">
              Inferred impact yaw: <strong>{photoYawEstimate.toFixed(1)}°</strong>
            </div>
            <p className="measure-note">
              Photo flow is the moat — Fletched and PCA can't match this because
              they're phone-input-only. v140.0 uses a simplified radius proxy;
              v140.1 wires the full v137 OpenCV detection pipeline.
            </p>
          </div>
        )}

        <footer className="measure-footer">
          <button type="button" className="cta-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="cta-primary" onClick={apply}>
            Apply ({yawToApply.toFixed(1)}°)
          </button>
        </footer>
      </div>
    </div>
  );
}
